import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Pagination,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TppEventSortField, TppEventSummary } from '../../../services/transitPassPool.service';
import { fetchTppEventsPage } from '../../../services/transitPassPool.service';
import {
  loadHistoryColumnLayout,
  defaultHistoryColumnLayout,
  saveHistoryColumnLayout,
  TPP_HISTORY_ALL_COLUMNS,
  TPP_HISTORY_SORTABLE_COLUMNS,
  withUsageContextColumns,
  type TppHistoryColumnId,
} from '../../../utils/tppHistoryTableLayout';
import { useTppMdUp } from './useTppMdUp';
import TppHistoryColumnsModal from './TppHistoryColumnsModal';
import TppHistoryEmptyState from './TppHistoryEmptyState';
import TppHistoryMobileCards from './TppHistoryMobileCards';
import { defaultSortDir, tppEventNeedsProofUi } from './tppHistoryFormat';
import { useTppDebouncedTrim } from './useTppDebouncedTrim';
import { renderTppHistoryBodyCell, renderTppHistoryHeaderCell } from './tppHistoryCells';

const HISTORY_PAGE_SIZE = 100;

type TppCardHistoryTableProps = {
  cardId: number;
  displayNumber: number;
  /** プール1枚のとき見出しを「履歴」にし、選択解除を出さない */
  soloCard?: boolean;
  poolKey: string;
  amountPrefix: string;
  amountSuffix: string;
  formatAbsAmount: (n: number) => string;
  onEditEvent: (ev: TppEventSummary) => void;
  onUploadProof: (eventId: number, file: File) => void;
  uploadProofPending: boolean;
  uploadProofEventId?: number;
  uploadProofError: boolean;
  onViewProof: (eventId: number) => void;
  proofViewerLoading: boolean;
  /** 親が使い方ガイド等の表示制御に使う */
  onEventsTotalChange?: (total: number) => void;
  /** 履歴ゼロ文面用（操作ボタンはカード行） */
  emptyCanCheckout?: boolean;
  emptyCanCharge?: boolean;
  emptyInactive?: boolean;
  /** 履歴パネルを閉じる（カード選択解除） */
  onClearSelection?: () => void;
};

export default function TppCardHistoryTable({
  cardId,
  displayNumber,
  soloCard = false,
  poolKey,
  amountPrefix,
  amountSuffix,
  formatAbsAmount,
  onEditEvent,
  onUploadProof,
  uploadProofPending,
  uploadProofEventId,
  uploadProofError,
  onViewProof,
  proofViewerLoading,
  onEventsTotalChange,
  emptyCanCheckout = false,
  emptyCanCharge = false,
  emptyInactive = false,
  onClearSelection,
}: TppCardHistoryTableProps) {
  const { t, i18n } = useTranslation();
  const mdUp = useTppMdUp();
  const useJaDates = (i18n.language || 'ja').startsWith('ja');

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, flushSearch] = useTppDebouncedTrim(searchInput);
  const [sortBy, setSortBy] = useState<TppEventSortField>('outDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [visibleColumns, setVisibleColumns] = useState<TppHistoryColumnId[]>(() => loadHistoryColumnLayout());
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [draftColumns, setDraftColumns] = useState<TppHistoryColumnId[]>(visibleColumns);

  useEffect(() => {
    setPage(1);
    setSearchInput('');
    flushSearch('');
    setSortBy('outDate');
    setSortDir('desc');
    // カード切替時のみリセット（flush の identity 変化では再実行しない）
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cardId/poolKey
  }, [cardId, poolKey]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const eventsQuery = useQuery({
    queryKey: ['tpp-events', cardId, poolKey, page, searchQuery, sortBy, sortDir],
    queryFn: () =>
      fetchTppEventsPage(cardId, {
        poolKey,
        page,
        pageSize: HISTORY_PAGE_SIZE,
        search: searchQuery || undefined,
        sortBy,
        sortDir,
      }),
  });

  const events = eventsQuery.data?.items ?? [];
  const total = eventsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));
  const pageStart = total === 0 ? 0 : (page - 1) * HISTORY_PAGE_SIZE + 1;
  const pageEnd = Math.min(page * HISTORY_PAGE_SIZE, total);
  const historyEmptyFresh = total === 0 && !searchQuery && !eventsQuery.isLoading;
  const usageContextCols = useMemo(
    () => ({
      hasPurpose: events.some((e) => Boolean(e.purpose?.trim())),
      hasRoute: events.some((e) => Boolean(e.route?.trim())),
      needsProofCol: events.some((e) => tppEventNeedsProofUi(e)),
      needsInDate: events.some((e) => e.eventKind === 'usage'),
    }),
    [events]
  );
  const displayColumns = useMemo(
    () => withUsageContextColumns(visibleColumns, usageContextCols),
    [visibleColumns, usageContextCols]
  );

  useEffect(() => {
    if (eventsQuery.isLoading) return;
    onEventsTotalChange?.(total);
  }, [total, eventsQuery.isLoading, onEventsTotalChange]);

  const applySearch = () => flushSearch();

  const clearSearch = () => {
    setSearchInput('');
    flushSearch('');
  };

  const handleSort = (col: TppHistoryColumnId) => {
    if (!TPP_HISTORY_SORTABLE_COLUMNS.includes(col)) {
      return;
    }
    const field = col as TppEventSortField;
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(defaultSortDir(col));
    }
    setPage(1);
  };

  const sortIndicator = (col: TppHistoryColumnId) => {
    if (!TPP_HISTORY_SORTABLE_COLUMNS.includes(col)) {
      return null;
    }
    const field = col as TppEventSortField;
    if (sortBy !== field) {
      return null;
    }
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const openColumnsModal = () => {
    setDraftColumns([...visibleColumns]);
    setColumnsModalOpen(true);
  };

  const toggleDraftColumn = (col: TppHistoryColumnId, checked: boolean) => {
    setDraftColumns((prev) => {
      if (checked) {
        if (prev.includes(col)) {
          return prev;
        }
        const next = [...prev, col];
        return TPP_HISTORY_ALL_COLUMNS.filter((c) => next.includes(c));
      }
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((c) => c !== col);
    });
  };

  const moveDraftColumn = (col: TppHistoryColumnId, dir: -1 | 1) => {
    setDraftColumns((prev) => {
      const idx = prev.indexOf(col);
      if (idx < 0) {
        return prev;
      }
      const target = idx + dir;
      if (target < 0 || target >= prev.length) {
        return prev;
      }
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const saveColumns = () => {
    setVisibleColumns(draftColumns);
    saveHistoryColumnLayout(draftColumns);
    setColumnsModalOpen(false);
  };

  const headerSort = {
    sortBy,
    sortDir,
    onSort: handleSort,
    sortIndicator,
  };

  const bodyCellCtx = {
    t,
    useJaDates,
    amountPrefix,
    amountSuffix,
    formatAbsAmount,
    onEditEvent,
    onUploadProof,
    uploadProofPending,
    uploadProofEventId,
    uploadProofError,
    onViewProof,
    proofViewerLoading,
  };

  return (
    <>
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-white py-3 border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
            <h3 className="h6 mb-0 d-flex align-items-center gap-2 flex-wrap">
              <span>
                {soloCard
                  ? t('tpp.history_title_solo')
                  : t('tpp.history_title', { n: displayNumber })}
              </span>
              {eventsQuery.isLoading && (
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </Spinner>
              )}
            </h3>
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end flex-grow-1 flex-md-grow-0">
              {onClearSelection && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={onClearSelection}
                  aria-label={t('tpp.clear_card_selection')}
                  title={t('tpp.clear_card_selection')}
                >
                  <i className="fas fa-times" aria-hidden />
                  <span className="d-none d-md-inline ms-1">
                    {t('tpp.clear_card_selection')}
                  </span>
                </Button>
              )}
              {/* 履歴があるときは検索を常時。列設定は表レイアウト時 */}
              {!historyEmptyFresh && (
                <>
                  <Form.Control
                    size="sm"
                    type="search"
                    className="flex-grow-1"
                    style={{ minWidth: '8rem', maxWidth: '22rem' }}
                    placeholder={
                      mdUp
                        ? t('tpp.history_search_placeholder')
                        : t('tpp.history_search_placeholder_short')
                    }
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        applySearch();
                      }
                    }}
                    aria-label={t('tpp.history_search_placeholder')}
                  />
                  <Button size="sm" variant="outline-primary" onClick={applySearch}>
                    {t('common.search')}
                  </Button>
                  {(!!searchQuery || searchInput.trim() !== '') && (
                    <Button
                      size="sm"
                      variant="link"
                      className="text-decoration-none px-1"
                      onClick={clearSearch}
                    >
                      {t('tpp.history_clear_search')}
                    </Button>
                  )}
                  {mdUp ? (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={openColumnsModal}
                      aria-label={t('tpp.history_columns_button')}
                      title={t('tpp.history_columns_button')}
                    >
                      <i className="fas fa-columns" aria-hidden />
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body className={historyEmptyFresh ? 'py-2 px-3 text-center' : 'p-0'}>
          {historyEmptyFresh ? (
            <TppHistoryEmptyState
              emptyCanCheckout={emptyCanCheckout}
              emptyCanCharge={emptyCanCharge}
              emptyInactive={emptyInactive}
            />
          ) : (
            <>
              {events.length === 0 && !eventsQuery.isLoading ? (
                <div className="text-center text-muted py-4 px-3">
                  <p className="mb-2">{t('tpp.history_no_results')}</p>
                  {!!searchQuery && (
                    <Button size="sm" variant="outline-secondary" onClick={clearSearch}>
                      {t('tpp.history_clear_search')}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {!mdUp ? (
                    <TppHistoryMobileCards
                      events={events}
                      useJaDates={useJaDates}
                      amountPrefix={amountPrefix}
                      amountSuffix={amountSuffix}
                      formatAbsAmount={formatAbsAmount}
                      onEditEvent={onEditEvent}
                      onUploadProof={onUploadProof}
                      uploadProofPending={uploadProofPending}
                      uploadProofEventId={uploadProofEventId}
                      uploadProofError={uploadProofError}
                      onViewProof={onViewProof}
                      proofViewerLoading={proofViewerLoading}
                    />
                  ) : (
                    <div className="table-responsive">
                      <Table striped hover size="sm" className="mb-0 bg-white">
                        <thead>
                          <tr>
                            {displayColumns.map((col) =>
                              renderTppHistoryHeaderCell(col, t, headerSort)
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((ev) => (
                            <tr key={ev.id}>
                              {displayColumns.map((col) =>
                                renderTppHistoryBodyCell(col, ev, bodyCellCtx)
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
              {totalPages > 1 && (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 border-top">
                  <span className="small text-muted">
                    {t('tpp.history_pagination_total', { total, start: pageStart, end: pageEnd })}
                  </span>
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    />
                    <Pagination.Item active>
                      {t('tpp.history_pagination_page', { page, totalPages })}
                    </Pagination.Item>
                    <Pagination.Next
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <TppHistoryColumnsModal
        show={columnsModalOpen}
        onHide={() => setColumnsModalOpen(false)}
        draftColumns={draftColumns}
        onToggleColumn={toggleDraftColumn}
        onMoveColumn={moveDraftColumn}
        onSave={saveColumns}
        onResetDraft={() => setDraftColumns(defaultHistoryColumnLayout())}
      />
    </>
  );
}
